import React from 'react'
import { motion } from 'framer-motion'
import { Store, Globe, Wallet, ExternalLink, CheckCircle2 } from 'lucide-react'
import { Platform } from '../../../types/macros'

interface PlatformStepProps {
  selectedPlatform: Platform | null
  onSelect: (platform: Platform) => void
}

interface PlatformOption {
  id: Platform
  title: string
  description: string
  icon: React.ReactNode
  link?: string
  linkText?: string
  features?: string[]
  badge?: string
}

const PlatformStep: React.FC<PlatformStepProps> = ({
  selectedPlatform,
  onSelect
}) => {
  const platforms: PlatformOption[] = [
    {
      id: Platform.SHOPIFY,
      title: 'MomentPerks for Shopify',
      description: 'Post-purchase offers on Thank You & Order Status pages',
      icon: <Store className="w-8 h-8" />,
      link: 'https://apps.shopify.com/adspostx-shopping-offers',
      linkText: 'View in Shopify App Store',
      features: [
        'Installs in minutes, no code needed',
        'Works on Thank You & Order Status pages',
        'Only first_name & last_name macros supported'
      ],
      badge: 'No-Code'
    },
    {
      id: Platform.MOMENTPERKS,
      title: 'MomentPerks',
      description: 'Direct SDK integration with full macro support',
      icon: <Globe className="w-8 h-8" />,
      features: [
        'Full macro functionality',
        'Complete data customization',
        'Flexible implementation'
      ],
      badge: 'Full Support'
    },
    {
      id: Platform.PERKSWALL,
      title: 'PerksWall',
      description: 'Standalone perks page with URL parameter macros',
      icon: <Wallet className="w-8 h-8" />,
      features: [
        'Shareable offer pages',
        'URL parameter passing',
        'No SDK integration needed'
      ],
      badge: 'Standalone'
    }
  ]

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Where will you use these macros?
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Select your integration platform to see available macro options
        </p>
      </div>

      {/* Platform Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        {platforms.map((platform) => {
          const isSelected = selectedPlatform === platform.id
          
          return (
            <motion.button
              key={platform.id}
              onClick={() => onSelect(platform.id)}
              className={`
                relative p-6 rounded-xl border-2 text-left transition-all
                ${isSelected
                  ? 'border-[#FE4215] bg-[#FE4215]/5 dark:bg-[#FE4215]/10'
                  : 'border-gray-200 dark:border-gray-700 hover:border-[#FE4215]/50'
                }
              `}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {/* Selection Indicator */}
              {isSelected && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute top-4 right-4 w-6 h-6 bg-[#FE4215] rounded-full flex items-center justify-center"
                >
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </motion.div>
              )}

              {/* Icon & Badge */}
              <div className="flex items-start justify-between mb-4">
                <div className={`
                  p-3 rounded-lg
                  ${isSelected ? 'bg-[#FE4215] text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'}
                `}>
                  {platform.icon}
                </div>
                {platform.badge && (
                  <span className="px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                    {platform.badge}
                  </span>
                )}
              </div>

              {/* Content */}
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {platform.title}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                {platform.description}
              </p>

              {/* Features */}
              {platform.features && platform.features.length > 0 && (
                <ul className="space-y-1.5 mb-3">
                  {platform.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1.5 mt-0.5 flex-shrink-0 text-green-600 dark:text-green-400" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              )}

              {/* Link */}
              {platform.link && (
                <a
                  href={platform.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center text-xs text-[#FE4215] hover:underline mt-2"
                >
                  {platform.linkText || 'Learn more'}
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              )}
            </motion.button>
          )
        })}
      </div>

      {/* Help Text */}
      <div className="text-center">
        <p className="text-sm text-gray-500 dark:text-gray-500">
          Not sure which to choose? <a href="https://docs.momentscience.com/implementing-macros" target="_blank" rel="noopener noreferrer" className="text-[#FE4215] hover:underline">
            View documentation
          </a>
        </p>
      </div>
    </div>
  )
}

export default PlatformStep
